import type { FeedbackFormConfiguration } from './types'

/**
 * Pure UDF reducer for the Feedback form. House style — same shape as
 * `metroMapStore` / the Suggester store (plain reducer + factory, no
 * Zustand). 1:1 behaviour port of iOS `FeedbackSystem`.
 *
 * The store owns ONLY the form's local interaction state (selected reason
 * ids + note + the post-submit ThankYou latch). Submission itself is a
 * pure derivation (`submitPayload`) the view forwards to the host's
 * analytics sink + close callback — there is NO network (analytics-only,
 * mirrors iOS).
 */
export interface FeedbackState {
  readonly config: FeedbackFormConfiguration
  /** Selected reason ids (multi-select; order-independent). */
  readonly selectedReasonIds: ReadonlySet<string>
  readonly note: string
  /** `true` after a successful submit — the view swaps to ThankYou. */
  readonly showingThankYou: boolean
}

export type FeedbackEvent =
  | { kind: 'ReasonToggled'; reasonId: string }
  | { kind: 'NoteChanged'; note: string }
  | { kind: 'Submitted' }

export function initialFeedbackState(config: FeedbackFormConfiguration): FeedbackState {
  return {
    config,
    selectedReasonIds: new Set(),
    note: '',
    showingThankYou: false,
  }
}

export function feedbackReducer(state: FeedbackState, event: FeedbackEvent): FeedbackState {
  switch (event.kind) {
    case 'ReasonToggled': {
      const next = new Set(state.selectedReasonIds)
      if (next.has(event.reasonId)) {
        next.delete(event.reasonId)
      } else {
        next.add(event.reasonId)
      }
      return { ...state, selectedReasonIds: next }
    }
    case 'NoteChanged':
      return { ...state, note: event.note }
    case 'Submitted':
      // Guard mirrors iOS `guard state.canSubmit`: a submit with nothing
      // selected and no note is a no-op (the button is also disabled).
      if (!canSubmit(state)) return state
      return { ...state, showingThankYou: true }
  }
}

/**
 * Submit is enabled iff at least one reason is selected OR the note has
 * non-whitespace content (mirrors iOS `State.canSubmit`).
 */
export function canSubmit(state: FeedbackState): boolean {
  return state.selectedReasonIds.size > 0 || state.note.trim().length > 0
}

/**
 * Longest note the `feedback_submitted` event carries. Mirrors iOS
 * `FeedbackPayload.analyticsNoteLimit` / Android `ANALYTICS_NOTE_LIMIT`.
 *
 * The note field is unbounded in the UI, so a rider can paste an arbitrarily
 * long text. Analytics backends cap an event's attribute payload and drop or
 * truncate what does not fit — silently. Capping here keeps the truncation
 * visible and under our control: `note` is the (possibly shortened) text,
 * `note_length` stays the ORIGINAL length, so a value above this cap means
 * the text was cut.
 */
export const ANALYTICS_NOTE_LIMIT = 1000

/**
 * The `feedback_submitted` analytics payload derived from state. Reason
 * TITLES are resolved from the config, sorted, and comma-joined — matching
 * iOS `selectedReasonTitles()` (sorted) + the event params
 * `{ reasons, has_note, note_length, note }`. The note is trimmed, and
 * capped at `ANALYTICS_NOTE_LIMIT` for the `note` param only.
 */
export function submitPayload(state: FeedbackState): {
  reasons: string
  has_note: boolean
  note_length: number
  note: string
} {
  const titleById = new Map<string, string>()
  for (const category of state.config.categories) {
    for (const reason of category.reasons) titleById.set(reason.id, reason.title)
  }
  const titles = [...state.selectedReasonIds]
    .map((id) => titleById.get(id))
    .filter((t): t is string => t !== undefined)
    .sort()
  const note = state.note.trim()
  return {
    reasons: titles.join(', '),
    has_note: note.length > 0,
    note_length: note.length,
    note: note.length <= ANALYTICS_NOTE_LIMIT ? note : note.slice(0, ANALYTICS_NOTE_LIMIT),
  }
}
